/* eslint-disable jira/naming/disallow-filenames */
// eslint-disable-next-line jira/filename-case
import React, { useCallback } from 'react';
import Link from '@atlaskit/link/link';
import {
	dismissFlag as createDismissFlag,
	type FlagComponentProps,
	ErrorFlag,
	useFlagService,
} from '@atlassian/jira-flags';
import { useIntl, defineMessage } from '@atlassian/jira-intl';
import { ProductDisplayNames, type CrossJoinTargetProduct } from '../../common/types.tsx';
import { generateId } from '../../utils/index.tsx';

const title = defineMessage({
	id: 'cross-join-nudges.request-access-error-flag.title',
	defaultMessage: "We couldn't add you to {productDisplayName}",
	description: 'Title of invite error flag describing if the request access was not successful',
});

const description = defineMessage({
	id: 'cross-join-nudges.request-access-error-flag.description',
	defaultMessage: "Try again in a few minutes. If you still aren't able to join, ",
	description: 'Description informing the user that request to join was not successfully sent',
});

const action = defineMessage({
	id: 'cross-join-nudges.request-access-error-flag.action',
	defaultMessage: 'contact support',
	description: 'Action to contact suport that redirects the user to the support page',
});

export const useRequestAccessErrorFlag = (): ((product: CrossJoinTargetProduct) => void) => {
	const { showFlag, dismissFlag } = useFlagService();
	return useCallback(
		(product: CrossJoinTargetProduct) => {
			const id = `error-${generateId()}`;
			const dismiss = () => dismissFlag(createDismissFlag(id));

			showFlag({
				id,
				render: (props) => (
					<RequestAccessErrorFlag {...props} id={id} product={product} onDismiss={dismiss} />
				),
			});
		},
		[dismissFlag, showFlag],
	);
};

export type Props = {
	id: FlagComponentProps['id'];
	product: CrossJoinTargetProduct;
	onDismiss: FlagComponentProps['onDismissed'];
};

const JIRA_SUPPORT_LINK = 'https://support.atlassian.com/contact/';

export const RequestAccessErrorFlag = ({ onDismiss, ...props }: Props): React.JSX.Element => {
	const { formatMessage } = useIntl();

	return (
		<ErrorFlag
			{...props}
			onDismissed={onDismiss}
			title={formatMessage(title, {
				productDisplayName: ProductDisplayNames[props.product],
			})}
			description={
				<>
					{/* eslint-disable-next-line @atlaskit/design-system/use-primitives-text */}
					<p>
						{formatMessage(description)}
						<Link href={JIRA_SUPPORT_LINK}>{formatMessage(action)}</Link>.
					</p>
				</>
			}
		/>
	);
};
